import { Skeleton } from '@mantine/core';
import type { ComponentType } from 'react';

import { getComponent } from '#core/components/dashboard/registry.ts';

/**
 * Generic fallback skeleton for dashboard components without a custom .Skeleton.
 *
 * Used by DashboardGrid's Suspense boundaries when a component doesn't define
 * its own skeleton via the createGridComponent() helper.
 */
export function BoxSkeleton() {
  return <Skeleton animate height="100%" radius="md" width="100%" />;
}

/**
 * Get the skeleton component for a registered component type.
 *
 * Components define their skeleton via the third argument to createGridComponent().
 * This function extracts the .Skeleton property from the component.
 *
 * @param type - The component type to look up
 * @returns The skeleton component if defined, undefined otherwise
 *
 * @example
 * const ChartSkeleton = getComponentSkeleton('chart');
 * if (ChartSkeleton) {
 *   return <ChartSkeleton />;
 * }
 */
export function ComponentSkeleton({ type }: { type: string }) {
  const entry = getComponent(type);
  if (!entry) {
    return <BoxSkeleton />;
  }
  // The component may have a .Skeleton property attached by createGridComponent()
  // biome-ignore lint/style/useNamingConvention: PascalCase required for JSX component reference
  const SkeletonComponent = entry.component as { Skeleton?: ComponentType<unknown> };
  if (!SkeletonComponent.Skeleton) {
    return <BoxSkeleton />;
  }
  return <SkeletonComponent.Skeleton />;
}
